import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import CredentialService from "../../_services/CredentialService";

interface ManifestIcon {
  id: string;
  name: string;
  category: string;
  path: string;
  tags?: string[];
}

interface Manifest {
  version: number;
  attribution?: string;
  icons: ManifestIcon[];
  categories: Array<{ id: string; name: string; description?: string }>;
}

interface Props {
  value: string;
  onChange: (url: string) => void;
  badgeDefinitionId?: number;
}

type Tab = "library" | "upload";

const BadgeImagePicker: React.FC<Props> = ({ value, onChange, badgeDefinitionId }) => {
  const [tab, setTab] = useState<Tab>("library");
  const [libraryUrl, setLibraryUrl] = useState<string>("");
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [manifestErr, setManifestErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await CredentialService.getBadgeLibraryUrl();
        const url = res?.data?.data?.libraryUrl;
        const manifestUrl = res?.data?.data?.manifestUrl;
        if (cancelled) return;
        setLibraryUrl(url || "");
        if (!manifestUrl) {
          setManifestErr("Library not configured");
          return;
        }
        const m = await fetch(manifestUrl, { cache: "force-cache" });
        if (!m.ok) {
          setManifestErr(`Library manifest unavailable (HTTP ${m.status})`);
          return;
        }
        const data: Manifest = await m.json();
        if (!cancelled) setManifest(data);
      } catch (e: any) {
        if (!cancelled) setManifestErr(e?.message || "Failed to load library");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredIcons = (manifest?.icons || []).filter((icon) => {
    if (activeCategory && icon.category !== activeCategory) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      icon.name.toLowerCase().includes(q) ||
      icon.category.toLowerCase().includes(q) ||
      (icon.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  const resolveUrl = (path: string) => `${libraryUrl}${path}`;

  const handleFile = async (file: File) => {
    setUploading(true);
    setUploadErr(null);
    try {
      const res: any = await CredentialService.uploadBadgeImage(file, badgeDefinitionId);
      const url = res?.data?.data?.url;
      if (!url) {
        setUploadErr("Upload succeeded but no URL was returned");
        return;
      }
      onChange(url);
    } catch (e: any) {
      const status = e?.response?.status;
      const code = e?.response?.data?.error?.code;
      if (status === 412 || code === "QUESTION_BUCKET_NOT_CONFIGURED") {
        setUploadDisabled(true);
        setUploadErr(
          "Custom badge uploads are not enabled for this tenant. Contact an admin to configure the public bucket.",
        );
      } else {
        setUploadErr(e?.response?.data?.error?.message || e?.message || "Upload failed");
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      {value ? (
        <div className="bm-img-selected">
          <img src={value} alt="Selected badge" className="bm-img-selected-thumb" />
          <div className="bm-img-selected-meta">
            <div className="bm-img-selected-label">Selected image</div>
            <div className="bm-img-selected-url">{value}</div>
          </div>
          <button type="button" className="bm-img-selected-clear" onClick={() => onChange("")}>
            Clear
          </button>
        </div>
      ) : null}

      <div className="bm-segctrl" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "library"}
          className={`bm-segctrl-item ${tab === "library" ? "is-active" : ""}`}
          onClick={() => setTab("library")}
        >
          Pick from library
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "upload"}
          disabled={uploadDisabled}
          className={`bm-segctrl-item ${tab === "upload" ? "is-active" : ""}`}
          onClick={() => setTab("upload")}
          title={uploadDisabled ? "Custom uploads not enabled for this tenant" : ""}
        >
          Upload custom
        </button>
      </div>

      {tab === "library" ? (
        manifestErr ? (
          <div className="bm-warn">
            {manifestErr}. You can still paste a URL below or switch to Upload custom.
          </div>
        ) : !manifest ? (
          <div className="bm-icon-empty">Loading library…</div>
        ) : (
          <>
            <div className="bm-chips">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                className={`bm-chip ${activeCategory === null ? "is-active" : ""}`}
              >
                All
              </button>
              {manifest.categories.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setActiveCategory(c.id)}
                  className={`bm-chip ${activeCategory === c.id ? "is-active" : ""}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <Form.Control
              type="text"
              placeholder="Search the library…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ marginBottom: 10 }}
            />
            <div className="bm-icon-grid">
              {filteredIcons.length === 0 ? (
                <div className="bm-icon-empty">No icons match your search.</div>
              ) : (
                filteredIcons.map((icon) => {
                  const url = resolveUrl(icon.path);
                  const selected = value === url;
                  return (
                    <button
                      type="button"
                      key={icon.id}
                      onClick={() => onChange(url)}
                      title={icon.name}
                      className={`bm-icon-tile ${selected ? "is-active" : ""}`}
                    >
                      <img src={url} alt={icon.name} />
                    </button>
                  );
                })
              )}
            </div>
            {manifest.attribution ? (
              <Form.Text className="text-muted" style={{ marginTop: 6 }}>
                {manifest.attribution}
              </Form.Text>
            ) : null}
          </>
        )
      ) : (
        <div className="bm-upload-zone">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            disabled={uploading || uploadDisabled}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
          {uploading ? <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-muted)" }}>Uploading…</div> : null}
          {uploadErr ? <div className="bm-error">{uploadErr}</div> : null}
          <Form.Text className="text-muted" style={{ display: "block", marginTop: 6 }}>
            PNG, JPEG, WEBP, or SVG. Max 5 MB.
          </Form.Text>
        </div>
      )}

      <Form.Control
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="bm-url-paste"
      />
    </div>
  );
};

export default BadgeImagePicker;
